'use client';

import { BlocksRenderer } from '@strapi/blocks-react-renderer';
import type { BlocksContent } from '@strapi/blocks-react-renderer';
import { useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import { FormErrorBox } from '../FormErrorBox';
import { validateAttachments } from '../validation';
import type { StepProps } from './stepProps';
import { StepShell } from './StepShell';
import {
  ACCEPTED_FILE_EXTENSIONS,
  MAX_FILE_SIZE_BYTES,
  validateFile,
} from '@/components/form/FileInput';

const SLOT_COUNT = 5;
const ACCEPT_ATTR = ACCEPTED_FILE_EXTENSIONS.join(',');

interface Step6Props extends StepProps {
  privacyText: BlocksContent;
  isSubmitting: boolean;
  onSubmit: () => void;
}

/**
 * Step 6 — Anhänge (AC 7). Five upload slots in a 2+2+1 grid, at least one
 * file required, plus the consent checkbox and the paper-application
 * disclaimer.
 *
 * A slot is a filled pill: empty it holds a "Datei auswählen" button and
 * "Keine ausgewählt"; once a file is picked it shows the filename with an X to
 * remove it, per the design.
 *
 * The slots are plain inputs rather than the form-context `FileInput`, because
 * this step's state lives in the reducer rather than in a TanStack form. The
 * validation rules are imported from `FileInput` so they stay defined once.
 */
export const Step6Attachments = ({
  state,
  dispatch,
  chrome,
  onBack,
  privacyText,
  isSubmitting,
  onSubmit,
}: Step6Props) => {
  const t = useTranslations('tutorApplication');
  const [consent, setConsent] = useState(false);
  const [slotErrors, setSlotErrors] = useState<(string | null)[]>(
    Array.from({ length: SLOT_COUNT }, () => null),
  );
  const [stepErrors, setStepErrors] = useState<
    ReturnType<typeof validateAttachments>
  >({});
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const setFile = (index: number, file: File | null) => {
    const files = [...state.files];
    files[index] = file;
    dispatch({ type: 'SET_FILES', files });
  };

  const clearNativeInput = (index: number) => {
    const input = inputRefs.current[index];
    if (input) {
      input.value = '';
    }
  };

  const handlePick = (index: number, file: File | null) => {
    setStepErrors({});

    if (file === null) {
      setFile(index, null);
      return;
    }

    const problem = validateFile(file);
    setSlotErrors((prev) => prev.map((e, i) => (i === index ? problem : e)));

    if (problem !== null) {
      // Reject without storing, and clear the native input so the applicant
      // can re-pick the same filename after fixing it.
      setFile(index, null);
      clearNativeInput(index);
      return;
    }

    setFile(index, file);
  };

  const handleRemove = (index: number) => {
    setFile(index, null);
    setSlotErrors((prev) => prev.map((e, i) => (i === index ? null : e)));
    clearNativeInput(index);
  };

  const handleSubmit = () => {
    const problems = validateAttachments(state, consent);
    setStepErrors(problems);
    if (Object.keys(problems).length === 0) {
      onSubmit();
    }
  };

  const maxSizeMb = Math.round(MAX_FILE_SIZE_BYTES / (1024 * 1024));
  const hasError =
    Object.keys(stepErrors).length > 0 || slotErrors.some(Boolean);

  return (
    <StepShell
      heading={t('step6Heading')}
      chrome={chrome}
      onBack={onBack}
      onNext={handleSubmit}
      hasError={hasError}
      isSubmitting={isSubmitting}
    >
      <p className="text-base">{t('formatHint', { maxSizeMb })}</p>

      {stepErrors.files && <FormErrorBox>{t(stepErrors.files)}</FormErrorBox>}

      <div className="grid gap-4 md:grid-cols-2">
        {Array.from({ length: SLOT_COUNT }, (_, index) => {
          const file = state.files[index] ?? null;
          const slotError = slotErrors[index];
          const slotNumber = index + 1;

          return (
            <div key={index}>
              <div className="rounded-input flex items-center justify-between gap-3 bg-[#eae8e7] px-4 py-3">
                {file ? (
                  <>
                    <span className="truncate text-base">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => handleRemove(index)}
                      aria-label={t('fileRemoveAria', { index: slotNumber })}
                      className="text-primary px-2 font-bold"
                    >
                      X
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => inputRefs.current[index]?.click()}
                      // The visible text repeats across all five slots, so the
                      // accessible name carries the slot number.
                      aria-label={t('filePickAria', { index: slotNumber })}
                      className="border-foreground/40 rounded border bg-transparent px-3 py-1 text-base"
                    >
                      {t('filePick')}
                    </button>
                    <span className="text-foreground/60 truncate text-base">
                      {t('fileNone')}
                    </span>
                  </>
                )}
              </div>

              <input
                ref={(el) => {
                  inputRefs.current[index] = el;
                }}
                type="file"
                accept={ACCEPT_ATTR}
                className="hidden"
                onChange={(e) => handlePick(index, e.target.files?.[0] ?? null)}
              />

              {slotError && (
                <p className="mt-1 text-sm text-red-600">{slotError}</p>
              )}
            </div>
          );
        })}
      </div>

      <label className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => {
            setConsent(e.target.checked);
            setStepErrors({});
          }}
          className="mt-1 h-5 w-5 flex-shrink-0"
        />
        <span className="text-sm">
          {/* The paragraph block is rendered inline so the required-marker
              stays on the same line as the end of the notice, instead of
              being pushed below it by the default <p>. */}
          <BlocksRenderer
            content={privacyText}
            blocks={{ paragraph: ({ children }) => <>{children}</> }}
          />{' '}
          *
        </span>
      </label>

      {stepErrors.consent && (
        <FormErrorBox>{t(stepErrors.consent)}</FormErrorBox>
      )}

      <p className="text-base">{t('paperNotice')}</p>
    </StepShell>
  );
};
